import { Link } from "react-router-dom";
import { useAcademy } from "@/context/AcademyContext";
import { PATHS, LEVELS, TOTAL_LESSONS } from "@/data/academy";
import { Card } from "@/components/ui-kit";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  GraduationCap, BookOpen, CheckCircle2, Circle, ChevronRight, Factory, Bot, Brain,
  UserCheck, Network, Radio, Plug, ShieldAlert, Lock, Route, ClipboardCheck, MessagesSquare, Languages,
} from "lucide-react";

const ICONS = {
  factory: Factory, bot: Bot, brain: Brain, "user-check": UserCheck, network: Network,
  radio: Radio, plug: Plug, "shield-alert": ShieldAlert, lock: Lock, route: Route,
  "clipboard-check": ClipboardCheck, "messages-square": MessagesSquare,
};
const TAG_TONE = {
  MVP: "text-emerald-400 border-emerald-800 bg-emerald-950/30",
  FUTURE: "text-blue-400 border-blue-800 bg-blue-950/30",
  CONCEPT: "text-violet-400 border-violet-800 bg-violet-950/30",
};
const LANG_OPTS = [{ code: "de", label: "Deutsch" }, { code: "en", label: "English" }, { code: "pt", label: "Português" }];

export default function AcademyHome() {
  const { overallPct, isDone, completedSet, t, lang, setLang } = useAcademy();

  return (
    <div className="space-y-6 ngx-fade-in" data-testid="academy-home">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="h-12 w-12 rounded-md bg-blue-600/15 border border-blue-800 flex items-center justify-center">
            <GraduationCap className="h-6 w-6 text-blue-400" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight text-slate-50">{t("academyTitle")}</h1>
            <p className="text-sm text-slate-400 mt-1 max-w-xl">{t("academySubtitle")}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Languages className="h-4 w-4 text-slate-500" />
          <Select value={lang} onValueChange={setLang}>
            <SelectTrigger data-testid="language-select" className="h-10 w-40 bg-[#0A1128] border-slate-700 text-slate-200">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0A1128] border-slate-700 text-slate-100">
              {LANG_OPTS.map((l) => <SelectItem key={l.code} value={l.code}>{l.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Link to="/academy/glossary" data-testid="academy-glossary-link"
            className="h-10 px-4 flex items-center gap-2 rounded-md border border-slate-700 bg-[#0A1128] text-slate-300 hover:border-blue-500 text-sm">
            <BookOpen className="h-4 w-4" /> {t("glossary")}
          </Link>
        </div>
      </div>

      {/* Overall progress */}
      <Card className="p-6 relative overflow-hidden">
        <div className="absolute inset-0 ngx-grid-overlay opacity-15" />
        <div className="relative z-10">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">{t("learningProgress")}</span>
            <span className="font-mono text-3xl font-medium text-blue-400" data-testid="academy-progress-overall">{overallPct}% <span className="text-sm text-slate-500">{t("complete")}</span></span>
          </div>
          <div className="h-2.5 rounded-full bg-slate-800 overflow-hidden">
            <div className="h-full bg-blue-500 transition-all" style={{ width: `${overallPct}%` }} />
          </div>
          <div className="mt-3 text-xs text-slate-500 font-mono">{completedSet.size} / {TOTAL_LESSONS} {t("lessons")}</div>
          <div className="mt-5 flex flex-wrap gap-2">
            {LEVELS.map((lv) => (
              <span key={lv.id} className="text-[11px] font-mono px-2.5 py-1 rounded-sm border border-slate-700 text-slate-400">
                {t("level")} {lv.id} · {lv.name}
              </span>
            ))}
          </div>
        </div>
      </Card>

      {/* Learning paths */}
      <div>
        <h2 className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-3">{t("learningPaths")}</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {PATHS.map((p) => {
            const Icon = ICONS[p.icon] || GraduationCap;
            const done = p.lessons.filter((l) => isDone(l.id)).length;
            const pct = Math.round((done / p.lessons.length) * 100);
            const level = LEVELS.find((lv) => lv.id === p.level);
            return (
              <Card key={p.id} className="p-5" data-testid={`path-card-${p.id}`}>
                <div className="flex items-start gap-3">
                  <div className="h-11 w-11 rounded-md bg-blue-600/10 border border-blue-900 flex items-center justify-center shrink-0">
                    <Icon className="h-5 w-5 text-blue-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="font-display font-semibold text-slate-100">{p.title}</h3>
                      <span className="text-[10px] font-mono text-slate-500 shrink-0">L{p.level} · {level?.name}</span>
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5">{p.desc}</p>
                    <div className="mt-3 flex items-center gap-2">
                      <div className="flex-1 h-1.5 rounded-full bg-slate-800 overflow-hidden">
                        <div className="h-full bg-blue-500" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-[11px] font-mono text-slate-500">{done}/{p.lessons.length}</span>
                    </div>
                  </div>
                </div>
                <div className="mt-4 space-y-1.5">
                  {p.lessons.map((l) => (
                    <Link key={l.id} to={`/academy/lesson/${l.id}`} data-testid={`lesson-link-${l.id}`}
                      className="flex items-center gap-2.5 px-3 py-2 rounded-md hover:bg-slate-800/40 group">
                      {isDone(l.id)
                        ? <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
                        : <Circle className="h-4 w-4 text-slate-600 shrink-0" />}
                      <span className="text-sm text-slate-300 group-hover:text-slate-100 flex-1 truncate">{l.title}</span>
                      <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded-sm border ${TAG_TONE[l.tag]}`}>{l.tag}</span>
                      <ChevronRight className="h-4 w-4 text-slate-600 group-hover:text-blue-400" />
                    </Link>
                  ))}
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}
